import {
  createFileRoute,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { Header } from '@/components/Header';
import { EventAttendance } from '@/pages/EventAttendance';
import { useEvent } from '@/lib/hooks/useEvents';
import { useEventRsvps } from '@/lib/hooks/useRsvps';
import { useEventAttendance } from '@/lib/hooks/useAttendance';
import { useUserProfile } from '@/lib/hooks/useUserProfile';

export const Route = createFileRoute(
  '/_authenticated/events/$eventId/attendance'
)({
  validateSearch: (search: { tab?: string }) => {
    const allowedTabs = ['going', 'notGoing', 'attendance'] as const;
    const tab = allowedTabs.includes(search.tab as any)
      ? (search.tab as (typeof allowedTabs)[number])
      : 'going';
    return { tab };
  },
  component: EventAttendancePage,
});

function EventAttendancePage() {
  const { eventId } = Route.useParams();
  const { tab } = Route.useSearch();
  const eventIdNum = parseInt(eventId, 10);
  const router = useRouterState();
  const childRouteIds = new Set([
    '/_authenticated/events/$eventId/attendance/add',
  ]);
  const isChildRoute = router.matches.some((match) =>
    childRouteIds.has(match.routeId)
  );
  const { isAdmin, isLoading: isLoadingProfile } = useUserProfile();
  const {
    data: event,
    isLoading: isLoadingEvent,
    error: eventError,
  } = useEvent(eventIdNum);
  const {
    data: rsvps = [],
    isLoading: isLoadingRsvps,
    error: rsvpsError,
  } = useEventRsvps(eventIdNum);
  const {
    data: attendance = [],
    isLoading: isLoadingAttendance,
    error: attendanceError,
  } = useEventAttendance(eventIdNum);

  if (isChildRoute) {
    return <Outlet />;
  }

  if (
    isLoadingEvent ||
    isLoadingRsvps ||
    isLoadingAttendance ||
    isLoadingProfile
  ) {
    return (
      <>
        <Header enableBack additionalClasses="bg-slate-700" />
        <div className="flex justify-center items-center py-12">
          <p className="text-body text-foreground/60">Loading attendance...</p>
        </div>
      </>
    );
  }

  if (!isAdmin()) {
    return (
      <>
        <Header enableBack additionalClasses="bg-slate-700" />
        <div className="flex justify-center items-center py-12 px-4 text-center">
          <p className="text-body text-foreground/80">
            You do not have permission to view RSVPs for this event.
          </p>
        </div>
      </>
    );
  }

  if (eventError || rsvpsError || attendanceError || !event) {
    return (
      <>
        <Header enableBack additionalClasses="bg-slate-700" />
        <div className="flex justify-center items-center py-12 px-4 text-center">
          <p className="text-body text-foreground/80">
            {eventError
              ? 'Failed to load event.'
              : rsvpsError
                ? 'Failed to load RSVPs.'
                : attendanceError
                  ? 'Failed to load attendance.'
                  : 'Event not found.'}
          </p>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col">
      <Header enableBack additionalClasses="bg-slate-700" />
      <EventAttendance
        event={event}
        rsvps={rsvps}
        attendance={attendance}
        initialTab={tab}
      />
    </div>
  );
}

